// Botão de amizade, o mesmo em todas as telas que mostram outro jogador. O estado vem da API (loja de relações):
// Adicionar amigo, Pedido enviado (o toque pergunta "Desfazer pedido?"), Aceitar amizade e Recusar, ou amigos.
// Bot, você mesmo, convidado e quem está bloqueado não têm o botão. Convidado que toca é levado a guardar a conta
// com Google; na volta, o pedido sai sozinho.
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { ErroDaApi } from './api.ts';
import { aceitarPedido, desfazerPedido, lerPedidos, pedirAmizade, recusarPedido, type Relacao } from './amigos.ts';
import { avisarNaTela } from './aviso.tsx';
import { medir } from './medicao.ts';
import { atualizarPresenca } from './presenca.ts';
import { definirRelacao, sessaoDasRelacoes, useRelacao } from './relacoes.ts';

// Ícones de pessoa: com +, com relógio e com ✓. Duas pessoas é o ícone de amigos nas listas.
function Pessoa({ children }: { children?: React.ReactNode }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="8" r="4" /><path d="M2 21c0-4 3-7 7-7 1.6 0 3 .4 4.2 1.2" />{children}
    </svg>
  );
}
export const IconePessoaMais = () => <Pessoa><path d="M19 13v7M15.5 16.5h7" /></Pessoa>;
export const IconePessoaRelogio = () => <Pessoa><circle cx="18.5" cy="17" r="4" /><path d="M18.5 15v2l1.3 1.2" /></Pessoa>;
export const IconePessoaCheck = () => <Pessoa><path d="M15.5 17.5l2.2 2.2 4.3-4.6" /></Pessoa>;
export function IconeAmigos({ tamanho = 18 }: { tamanho?: number }) {
  return (
    <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="8" r="4" /><path d="M2 21c0-4 3-7 7-7s7 3 7 7M16 4a4 4 0 0 1 0 8M22 21c0-3-2-5.5-5-6.5" />
    </svg>
  );
}

type Dialogo = 'desfazer' | null;

export function BotaoAmizade({ usuario, apelido, compacto = false }: { usuario: string; apelido: string; compacto?: boolean }) {
  const estado = useRelacao(usuario);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [ocupado, setOcupado] = useState(false);
  const s = sessaoDasRelacoes();
  // A conta anônima de antes (só na mesa em que estava sentada) não tem o botão.
  if (!s || s.anonimo || !estado || estado === 'indisponivel' || estado === 'bloqueado' || usuario === s.id) return null;

  async function rodar(antes: Relacao, depois: Relacao | null, fazer: () => Promise<{ estado: Relacao }>) {
    if (ocupado) return;
    setOcupado(true);
    if (depois) definirRelacao(usuario, depois); // muda na hora; volta se a API recusar
    try {
      const r = await fazer();
      definirRelacao(usuario, r.estado);
      if (antes === 'pedido_recebido') atualizarPresenca(); // o ponto de pedidos da navegação acompanha
      if (antes === 'pedido_recebido' && r.estado === 'amigos') medir('amizade_aceita', { origem: 'pedido' });
    } catch (e) {
      definirRelacao(usuario, antes);
      avisarNaTela(e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.');
    } finally { setOcupado(false); }
  }
  // O pedido do par, para aceitar, recusar ou desfazer.
  async function pedidoCom(lado: 'recebidos' | 'enviados') {
    const p = (await lerPedidos(s!))[lado].find((x) => x.usuario === usuario);
    if (!p) throw new ErroDaApi(404, 'inexistente', 'Esse pedido não existe mais.');
    return p.id;
  }

  const rotulo = (texto: string) => (compacto ? null : <span>{texto}</span>);
  let conteudo: React.ReactNode;
  if (estado === 'amigos') {
    conteudo = (
      <span className={'amizade amigos' + (compacto ? ' compacto' : '')} role="img" aria-label={`${apelido} é seu amigo`}>
        <IconePessoaCheck />{rotulo('Amigos')}
      </span>
    );
  } else if (estado === 'pedido_recebido') {
    conteudo = (
      <span className={'amizade-resposta' + (compacto ? ' compacto' : '')}>
        <button className="amizade aceitar" disabled={ocupado} aria-label={`Aceitar amizade de ${apelido}`}
          onClick={() => void rodar('pedido_recebido', 'amigos', async () => aceitarPedido(s, await pedidoCom('recebidos')))}>
          <IconePessoaCheck />{rotulo('Aceitar amizade')}
        </button>
        <button className="amizade recusar" disabled={ocupado} aria-label={`Recusar o pedido de ${apelido}`}
          onClick={() => void rodar('pedido_recebido', 'nenhum', async () => recusarPedido(s, await pedidoCom('recebidos')))}>
          {compacto ? '✕' : 'Recusar'}
        </button>
      </span>
    );
  } else if (estado === 'pedido_enviado') {
    conteudo = (
      <button className={'amizade enviado' + (compacto ? ' compacto' : '')} aria-label={`Pedido enviado para ${apelido}. Toque para desfazer`}
        onClick={() => setDialogo('desfazer')}>
        <IconePessoaRelogio />{rotulo('Pedido enviado')}
      </button>
    );
  } else {
    conteudo = (
      <button className={'amizade' + (compacto ? ' compacto' : '')} aria-label={`Adicionar ${apelido} como amigo`} disabled={ocupado}
        onClick={() => void rodar('nenhum', 'pedido_enviado', () => pedirAmizade(s, usuario))}>
        <IconePessoaMais />{rotulo('Adicionar amigo')}
      </button>
    );
  }

  return (
    <>
      {conteudo}
      {dialogo && createPortal(
        <div className="dialogo-fundo" onClick={(ev) => { if (ev.target === ev.currentTarget) setDialogo(null); }}>
          <div className="dialogo" role="alertdialog" aria-modal="true" aria-labelledby="amizade-desfazer">
            <h2 id="amizade-desfazer">Desfazer pedido?</h2>
            <p>O pedido de amizade para {apelido} sai.</p>
            <button className="btn2 largo perigo" onClick={() => {
              setDialogo(null);
              void rodar('pedido_enviado', 'nenhum', async () => desfazerPedido(s, await pedidoCom('enviados')));
            }}>Desfazer pedido</button>
            <button className="btn2 largo" onClick={() => setDialogo(null)}>Manter</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
